import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useUpdateOrganization } from '../../hooks/useOrganizationManagement';
import {
  updateOrganizationSchema,
  type UpdateOrganizationInput,
} from '../../schemas/organizationSchemas';
import type { Organization } from '../../types';

interface EditOrganizationFormProps {
  organization: Organization;
  onClose: () => void;
  formId?: string;
}

export function EditOrganizationForm({
  organization,
  onClose,
  formId = 'edit-form',
}: EditOrganizationFormProps) {
  const updateOrganization = useUpdateOrganization();

  const form = useForm<UpdateOrganizationInput>({
    resolver: zodResolver(updateOrganizationSchema),
    defaultValues: {
      name: organization.name,
      is_active: organization.is_active,
    },
  });

  const onSubmit = async (data: UpdateOrganizationInput) => {
    try {
      await updateOrganization.mutateAsync({
        id: organization.id,
        data,
      });
      onClose();
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <Form {...form}>
      <form 
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)} 
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Starbucks"
                  data-testid="org-form-name"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active Status</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable or disable this organization
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="org-form-status"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
